import { mondayItemUrl } from './links.js';
import type { Deliverable, Epic, Metadata, PersonRef, ProductGoal, RoadmapSnapshot, StatusOption, Task } from './types.js';

/**
 * Synthetic data with the same structure as the Mona boards. Used by unit tests, the Playwright
 * suite and the web app's mock mode (VITE_MOCK_API=1). It contains no real monday content.
 */
export const BOARDS = { productGoals: '5105892412', epics: '5105892415', deliverables: '5105892420' };

const opt = (id: number, label: string, color: string, index: number, isDone = false): StatusOption => ({
  id,
  label,
  color,
  index,
  isDone,
});

export const people: PersonRef[] = [
  { id: '1001', name: 'Ada Lovelace', email: 'ada@example.test', kind: 'person' },
  { id: '1002', name: 'Grace Hopper', email: 'grace@example.test', kind: 'person' },
  { id: '1003', name: 'Linus Torvalds', email: 'linus@example.test', kind: 'person' },
];
const [ada, grace, linus] = people as [PersonRef, PersonRef, PersonRef];

export const metadata: Metadata = {
  options: {
    status: [
      opt(5, 'Not started', '#c4c4c4', 0),
      opt(0, 'In progress', '#fdab3d', 1),
      opt(2, 'Stuck', '#df2f4a', 2),
      opt(1, 'Done', '#00c875', 3, true),
    ],
    scopingStatus: [opt(5, 'Not started', '#c4c4c4', 0), opt(0, 'In progress', '#fdab3d', 1), opt(1, 'Done', '#00c875', 2, true)],
    sprint: [
      opt(0, 'Sprint 1 (10/11-10/15)', '#fdab3d', 0),
      opt(1, 'Sprint 2 (10/18-10/22)', '#00c875', 1),
      opt(2, 'Sprint 3 (10/25-10/29)', '#df2f4a', 2),
      opt(3, 'Sprint 4 (11/01-11/05)', '#007eb5', 3),
    ],
    productArea: [opt(0, 'Companion', '#fdab3d', 0), opt(3, 'Deployment', '#007eb5', 1), opt(11, 'Content', '#bb3354', 2)],
    pgStatus: [opt(5, 'Planned', '#c4c4c4', 0), opt(0, 'In progress', '#fdab3d', 1), opt(2, 'At risk', '#df2f4a', 2), opt(1, 'Done', '#00c875', 3, true)],
    type: [opt(3, 'external', '#007eb5', 0), opt(5, 'internal', '#c4c4c4', 1)],
    taskStatus: [opt(0, 'Working on it', '#fdab3d', 0), opt(1, 'Done', '#00c875', 1, true), opt(2, 'Stuck', '#df2f4a', 2)],
  },
  people,
  doneStatuses: ['Done'],
  accountUrl: 'https://monasecurity.monday.com',
  boards: BOARDS,
  readOnlyFields: ['productArea'],
};

const goal = (id: string, name: string, targetDate: string | null, position: number): ProductGoal => ({
  id,
  name,
  url: mondayItemUrl(BOARDS.productGoals, id),
  targetDate,
  status: 'Planned',
  description: null,
  position,
  updatedAt: '2026-10-13T06:00:00Z',
});

const epic = (id: string, name: string, productArea: string | null, position: number): Epic => ({
  id,
  name,
  url: mondayItemUrl(BOARDS.epics, id),
  productArea,
  type: 'external',
  pmOwners: [ada],
  rdOwners: [linus],
  description: null,
  position,
  updatedAt: '2026-10-13T06:00:00Z',
});

export const productGoals: ProductGoal[] = [
  goal('9001', 'PG-01 First customer installation readiness', '2026-10-22', 1),
  goal('9000', 'PG-00 DP MVP demo session', '2026-10-11', 0),
  goal('9003', 'PG-03 Expanded value above first installation', '2026-10-29', 3),
  goal('9004', 'TBD', null, 4),
  goal('9002', 'PG-02 DP deployment with full device mode', '2026-11-18', 2),
];

export const epics: Epic[] = [
  epic('8001', 'Browser Companion', 'Companion', 0),
  epic('8002', 'Policy Management', 'Companion', 1),
  epic('8003', 'Content: Phishing', 'Content', 2),
  epic('8004', 'Deployment - Full Device', 'Deployment', 3),
  epic('8005', 'Epic without deliverables', 'Content', 4),
];

let pos = 0;
const del = (
  id: string,
  name: string,
  goalIds: string[],
  epicIds: string[],
  status: string | null,
  extra: Partial<Deliverable> = {},
): Deliverable => {
  const epicArea = epics.find((e) => e.id === epicIds[0])?.productArea ?? null;
  return {
    id,
    name,
    url: mondayItemUrl(BOARDS.deliverables, id),
    key: `MON-${String(pos + 1).padStart(3, '0')}`,
    productGoalIds: goalIds,
    epicIds,
    productArea: epicArea,
    pmOwners: [ada],
    assignees: [],
    sprint: null,
    scopingStatus: 'Not started',
    scopingDue: null,
    status,
    type: 'internal',
    description: null,
    subitemCount: 0,
    position: pos++,
    updatedAt: '2026-10-13T06:00:00Z',
    ...extra,
  };
};

export const deliverables: Deliverable[] = [
  // The Browser Companion epic spans PG-01 and PG-03
  del('7001', 'Browser intervention v0', ['9001'], ['8001'], 'Done', { assignees: [grace], sprint: 'Sprint 1 (10/11-10/15)', scopingStatus: 'Done' }),
  del('7002', 'Link gating v0', ['9001'], ['8001'], 'In progress', { assignees: [linus], sprint: 'Sprint 2 (10/18-10/22)', scopingDue: '2026-10-15' }),
  del('7003', 'Posture intervention v0', ['9003'], ['8001'], 'Not started', { assignees: [ada], sprint: 'Sprint 3 (10/25-10/29)' }),
  del('7004', 'Policy v0', ['9001'], ['8002'], 'Done', { assignees: [ada, grace], sprint: 'Sprint 1 (10/11-10/15)' }),
  del('7005', 'Policy v1', ['9003'], ['8002'], 'Not started', { sprint: 'Sprint 4 (11/01-11/05)' }),
  del('7006', 'Content phishing in the browser v0.1', ['9003'], ['8003'], 'In progress', { sprint: 'Sprint 3 (10/25-10/29)' }),
  del('7007', 'Content phishing in the browser v0.2', ['9002'], ['8003'], 'Not started', { productArea: 'Content' }),
  del('7008', 'Device installation supports self-signed certificates', ['9002'], ['8004'], 'Stuck', { assignees: [linus] }),
  // Standalone (no epic) within a goal
  del('7009', 'Customer installation documentation ready', ['9001'], [], 'Not started', { type: 'external' }),
  del('7010', 'Clean CI/CD is operational', ['9001'], [], 'Done', { sprint: 'Sprint 1 (10/11-10/15)' }),
  // No product goal
  del('7011', 'Backlog idea: dark mode for the portal', [], ['8001'], null, { scopingStatus: null }),
  del('7012', 'Orphan deliverable', [], [], 'Not started'),
  // Demo PG has one deliverable with a different epic
  del('7013', 'Demo data in portal', ['9000'], [], 'Done'),
];

export const snapshot: RoadmapSnapshot = {
  productGoals,
  epics,
  deliverables,
  fetchedAt: '2026-10-13T07:00:00.000Z',
  cached: false,
};

export const tasks: Task[] = [
  { id: '6001', name: 'Write spec', url: mondayItemUrl('5105941835', '6001'), deliverableId: '7002', status: 'Done', owners: [ada], date: null },
  { id: '6002', name: 'Implement gate', url: mondayItemUrl('5105941835', '6002'), deliverableId: '7002', status: 'Working on it', owners: [linus], date: '2026-10-20' },
];
